// ============================================================
// SharedFilter — React port of Angular app-shared-filter component
// Renders filter checkboxes based on showFilters prop and emits filter state changes
// ============================================================
import React, { useCallback, useEffect, useState } from 'react';
import { Checkbox, CheckboxChangeEvent } from 'primereact/checkbox';
import { FilterState, FilterCode } from './filterTypes';

export interface SharedFilterProps {
  showFilters: FilterCode[];
  onFilterChange: (filters: FilterState) => void;
}

/** Build initial filter state based on which filter sections are visible */
function buildInitialState(showFilters: FilterCode[]): FilterState {
  const hasSchoolType =
    showFilters.includes('1') ||
    showFilters.includes('S1') ||
    showFilters.includes('S2');
  const hasManagement = showFilters.includes('2') || showFilters.includes('A2');
  const hasCategory = showFilters.includes('3');
  const hasGender = showFilters.includes('4');
  const hasCommunity = showFilters.includes('5');
  const isRadioGovt = showFilters.includes('R7');
  const hasClasses = showFilters.includes('6');
  return {
    // School Type — pre-checked when section is visible
    governmentChk: hasSchoolType || isRadioGovt,
    fullyAidedChk: hasSchoolType,
    partiallyAidedChk: hasSchoolType,
    unAidedChk: showFilters.includes('1') || showFilters.includes('S2'),
    centralGovtChk: showFilters.includes('1'),
    stateChk: false,

    // Category Type
    primarySchoolChk: hasCategory,
    middleSchoolChk: hasCategory,
    highSchoolChk: hasCategory,
    // highSecSchoolChk: hasCategory,

    // Management
    allMangeIdChk: hasManagement,
    schlDepMangeIdChk: hasManagement,
    corpSchoolChk: hasManagement,
    municipalSchoolChk: hasManagement,
    adiDravidaChk: hasManagement,
    forestDeptSchlChk: hasManagement,
    DAWDSChk: hasManagement,
    kallarDeptChk: hasManagement,
    tribalWelChk: hasManagement,
    borstalSchoolChk: hasManagement,
    KGBVChk: hasManagement,
    NSCBAVChk: hasManagement,

    // Gender
    male: hasGender,
    female: hasGender,

    // Community
    bcOthers: hasCommunity,
    bcMuslim: hasCommunity,
    mbc: hasCommunity,
    st: hasCommunity,
    scOthers: hasCommunity,
    SCArunthathiyar: hasCommunity,
    oc: hasCommunity,
    dnc: hasCommunity,
    socialCategory: hasCommunity,

    // Classes (not used by our 4 charts, but included for completeness)
    pre_kg: hasClasses,
    lkg: hasClasses,
    ukg: hasClasses,
    class1: hasClasses,
    class2: hasClasses,
    class3: hasClasses,
    class4: hasClasses,
    class5: hasClasses,
    class6: hasClasses,
    class7: hasClasses,
    class8: hasClasses,
    class9: hasClasses,
    class10: hasClasses,
    class11: hasClasses,
    class12: hasClasses,
  };
}

const SharedFilter: React.FC<SharedFilterProps> = ({
  showFilters,
  onFilterChange,
}) => {
  const [state, setState] = useState<FilterState>(() =>
    buildInitialState(showFilters)
  );

  // Derived visibility flags
  const showSchoolType = showFilters.includes('1');
  const showSchoolTypeThree = showFilters.includes('S1');
  const showSchoolTypeFour = showFilters.includes('S2');
  const showManagement = showFilters.includes('2');
  const showAllManagement = showFilters.includes('A2');
  const showCategory = showFilters.includes('3');
  const showGender = showFilters.includes('4');
  const showCommunity = showFilters.includes('5');
  const showClasses = showFilters.includes('6');
  const isRadioGovt = showFilters.includes('R7');

  // Management is disabled when Government is not checked
  const managementDisabled = !state.governmentChk;

  // Emit filter changes on every state update
  useEffect(() => {
    onFilterChange(state);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  /** Generic checkbox handler */
  const handleCheck = useCallback(
    (field: keyof FilterState, checked: boolean) => {
      setState((prev) => ({ ...prev, [field]: checked }));
    },
    []
  );

  /** School type change — also toggles management enabled/disabled logic */
  const handleSchoolTypeChange = useCallback(
    (field: keyof FilterState, checked: boolean) => {
      setState((prev) => {
        const next = { ...prev, [field]: checked };

        // If no school type is checked, disable management and uncheck all
        const anySchool =
          next.governmentChk ||
          next.fullyAidedChk ||
          next.partiallyAidedChk ||
          next.unAidedChk ||
          next.centralGovtChk;

        if (!anySchool) {
          // Clear management selections when no school type selected
          next.allMangeIdChk = false;
          next.schlDepMangeIdChk = false;
          next.corpSchoolChk = false;
          next.municipalSchoolChk = false;
          next.adiDravidaChk = false;
          next.forestDeptSchlChk = false;
          next.DAWDSChk = false;
          next.kallarDeptChk = false;
          next.tribalWelChk = false;
          next.borstalSchoolChk = false;
          next.KGBVChk = false;
          next.NSCBAVChk = false;
        } else if (next.governmentChk && !prev.governmentChk) {
          // If government just got checked, enable all management
          next.allMangeIdChk = true;
          next.schlDepMangeIdChk = true;
          next.corpSchoolChk = true;
          next.municipalSchoolChk = true;
          next.adiDravidaChk = true;
          next.forestDeptSchlChk = true;
          next.DAWDSChk = true;
          next.kallarDeptChk = true;
          next.tribalWelChk = true;
          next.borstalSchoolChk = true;
          next.KGBVChk = true;
          next.NSCBAVChk = true;
        } else if (!next.governmentChk && prev.governmentChk) {
          // If government just got unchecked, clear management
          next.allMangeIdChk = false;
          next.schlDepMangeIdChk = false;
          next.corpSchoolChk = false;
          next.municipalSchoolChk = false;
          next.adiDravidaChk = false;
          next.forestDeptSchlChk = false;
          next.DAWDSChk = false;
          next.kallarDeptChk = false;
          next.tribalWelChk = false;
          next.borstalSchoolChk = false;
          next.KGBVChk = false;
          next.NSCBAVChk = false;
        }

        return next;
      });
    },
    []
  );

  /** Management "All" toggle */
  const handleManagementAll = useCallback((checked: boolean) => {
    setState((prev) => ({
      ...prev,
      allMangeIdChk: checked,
      schlDepMangeIdChk: checked,
      corpSchoolChk: checked,
      municipalSchoolChk: checked,
      adiDravidaChk: checked,
      forestDeptSchlChk: checked,
      DAWDSChk: checked,
      kallarDeptChk: checked,
      tribalWelChk: checked,
      borstalSchoolChk: checked,
      KGBVChk: checked,
      NSCBAVChk: checked,
    }));
  }, []);

  /** Community "All" toggle */
  const handleCommunityAll = useCallback((checked: boolean) => {
    setState((prev) => ({
      ...prev,
      bcOthers: checked,
      bcMuslim: checked,
      mbc: checked,
      st: checked,
      scOthers: checked,
      SCArunthathiyar: checked,
      oc: checked,
      dnc: checked,
      socialCategory: checked,
    }));
  }, []);

  /** Check if all community options are selected */
  const allCommunityChecked =
    state.bcOthers &&
    state.bcMuslim &&
    state.mbc &&
    state.st &&
    state.scOthers &&
    state.SCArunthathiyar &&
    state.oc &&
    state.dnc &&
    state.socialCategory;

  const handleAllClasses = useCallback((checked: boolean) => {
    setState((prev) => ({
      ...prev,
      pre_kg: checked,
      lkg: checked,
      ukg: checked,
      class1: checked,
      class2: checked,
      class3: checked,
      class4: checked,
      class5: checked,
      class6: checked,
      class7: checked,
      class8: checked,
      class9: checked,
      class10: checked,
      class11: checked,
      class12: checked,
    }));
  }, []);

  const allClassesChecked =
    state.pre_kg &&
    state.lkg &&
    state.ukg &&
    state.class1 &&
    state.class2 &&
    state.class3 &&
    state.class4 &&
    state.class5 &&
    state.class6 &&
    state.class7 &&
    state.class8 &&
    state.class9 &&
    state.class10 &&
    state.class11 &&
    state.class12;

  // Helper to render a filter checkbox row
  const renderCheckbox = (
    label: string,
    field: keyof FilterState,
    onChange?: (checked: boolean) => void,
    disabled?: boolean
  ) => (
    <div className="shared-filter-item" key={field}>
      <Checkbox
        inputId={field}
        checked={state[field] as boolean}
        onChange={(e: CheckboxChangeEvent) => {
          const checked = e.checked ?? false;
          if (onChange) {
            onChange(checked);
          } else {
            handleCheck(field, checked);
          }
        }}
        disabled={disabled}
      />
      <label htmlFor={field} className="shared-filter-label">
        {label}
      </label>
    </div>
  );

  return (
    <div className="shared-filter-panel">
      <h6 className="shared-filter-title">Filters</h6>

      {/* School Type section (5 options) */}
      {showSchoolType && (
        <div className="shared-filter-section">
          <label className="shared-filter-section-label">School Type</label>
          <div className="shared-filter-options">
            {renderCheckbox('Government', 'governmentChk', (c) =>
              handleSchoolTypeChange('governmentChk', c)
            )}
            {renderCheckbox('Fully Aided', 'fullyAidedChk', (c) =>
              handleSchoolTypeChange('fullyAidedChk', c)
            )}
            {renderCheckbox('Partially Aided', 'partiallyAidedChk', (c) =>
              handleSchoolTypeChange('partiallyAidedChk', c)
            )}
            {renderCheckbox('Un-Aided', 'unAidedChk', (c) =>
              handleSchoolTypeChange('unAidedChk', c)
            )}
            {renderCheckbox('Central Government', 'centralGovtChk', (c) =>
              handleSchoolTypeChange('centralGovtChk', c)
            )}
          </div>
        </div>
      )}

      {/* School Type section (first 3 options) */}
      {showSchoolTypeThree && (
        <div className="shared-filter-section">
          <label className="shared-filter-section-label">School Type</label>
          <div className="shared-filter-options">
            {renderCheckbox('Government', 'governmentChk', (c) =>
              handleSchoolTypeChange('governmentChk', c)
            )}
            {renderCheckbox('Fully Aided', 'fullyAidedChk', (c) =>
              handleSchoolTypeChange('fullyAidedChk', c)
            )}
            {renderCheckbox('Partially Aided', 'partiallyAidedChk', (c) =>
              handleSchoolTypeChange('partiallyAidedChk', c)
            )}
          </div>
        </div>
      )}

      {/* School Type section (first 4 options) */}
      {showSchoolTypeFour && (
        <div className="shared-filter-section">
          <label className="shared-filter-section-label">School Type</label>
          <div className="shared-filter-options">
            {renderCheckbox('Government', 'governmentChk', (c) =>
              handleSchoolTypeChange('governmentChk', c)
            )}
            {renderCheckbox('Fully Aided', 'fullyAidedChk', (c) =>
              handleSchoolTypeChange('fullyAidedChk', c)
            )}
            {renderCheckbox('Partially Aided', 'partiallyAidedChk', (c) =>
              handleSchoolTypeChange('partiallyAidedChk', c)
            )}
            {renderCheckbox('Un-Aided', 'unAidedChk', (c) =>
              handleSchoolTypeChange('unAidedChk', c)
            )}
          </div>
        </div>
      )}

      {/* Radio Government (R7) — shows only Government pre-checked */}
      {isRadioGovt && (
        <div className="shared-filter-section">
          <label className="shared-filter-section-label">School Type</label>
          <div className="shared-filter-options">
            {renderCheckbox('Government', 'governmentChk', (c) =>
              handleSchoolTypeChange('governmentChk', c)
            )}
          </div>
        </div>
      )}

      {/* Management section */}
      {(showManagement || showAllManagement) && (
        <div className="shared-filter-section">
          <label className="shared-filter-section-label">Management</label>
          <div className="shared-filter-options shared-filter-scrollable">
            <div className="shared-filter-item">
              <Checkbox
                inputId="allMangeIdChk"
                checked={state.allMangeIdChk}
                onChange={(e: CheckboxChangeEvent) =>
                  handleManagementAll(e.checked ?? false)
                }
                disabled={managementDisabled}
              />
              <label htmlFor="allMangeIdChk" className="shared-filter-label">
                All
              </label>
            </div>
            {renderCheckbox(
              'School Education Dept',
              'schlDepMangeIdChk',
              undefined,
              managementDisabled
            )}
            {renderCheckbox(
              'Corporation',
              'corpSchoolChk',
              undefined,
              managementDisabled
            )}
            {renderCheckbox(
              'Municipal',
              'municipalSchoolChk',
              undefined,
              managementDisabled
            )}
            {renderCheckbox(
              'Adi-Dravida Welfare',
              'adiDravidaChk',
              undefined,
              managementDisabled
            )}
            {renderCheckbox(
              'Forest Department',
              'forestDeptSchlChk',
              undefined,
              managementDisabled
            )}
            {renderCheckbox('DAWDS', 'DAWDSChk', undefined, managementDisabled)}
            {renderCheckbox(
              'Kallar BC/MBC Dept',
              'kallarDeptChk',
              undefined,
              managementDisabled
            )}
            {renderCheckbox(
              'Tribal Welfare',
              'tribalWelChk',
              undefined,
              managementDisabled
            )}
            {renderCheckbox(
              'Borstal (Social Defence)',
              'borstalSchoolChk',
              undefined,
              managementDisabled
            )}
            {renderCheckbox('KGBV', 'KGBVChk', undefined, managementDisabled)}
            {renderCheckbox(
              'NSCBAV (ARS Schools)',
              'NSCBAVChk',
              undefined,
              managementDisabled
            )}
          </div>
        </div>
      )}

      {/* Category Type section */}
      {showCategory && (
        <div className="shared-filter-section">
          <label className="shared-filter-section-label">Category Type</label>
          <div className="shared-filter-options">
            {renderCheckbox('Primary School', 'primarySchoolChk')}
            {renderCheckbox('Middle School', 'middleSchoolChk')}
            {renderCheckbox('High School', 'highSchoolChk')}
            {/* {renderCheckbox('Hr.Sec School', 'highSecSchoolChk')} */}
          </div>
        </div>
      )}

      {/* Gender section */}
      {showGender && (
        <div className="shared-filter-section">
          <label className="shared-filter-section-label">Gender</label>
          <div className="shared-filter-options">
            {renderCheckbox('Boys', 'male')}
            {renderCheckbox('Girls', 'female')}
          </div>
        </div>
      )}

      {/* Community section */}
      {showCommunity && (
        <div className="shared-filter-section">
          <label className="shared-filter-section-label">Community</label>
          <div className="shared-filter-options shared-filter-scrollable">
            <div className="shared-filter-item">
              <Checkbox
                inputId="allCommunityChk"
                checked={allCommunityChecked}
                onChange={(e: CheckboxChangeEvent) =>
                  handleCommunityAll(e.checked ?? false)
                }
              />
              <label htmlFor="allCommunityChk" className="shared-filter-label">
                All
              </label>
            </div>
            {renderCheckbox('BC-Others', 'bcOthers')}
            {renderCheckbox('BC-Muslim', 'bcMuslim')}
            {renderCheckbox('MBC', 'mbc')}
            {renderCheckbox('ST', 'st')}
            {renderCheckbox('SC-Others', 'scOthers')}
            {renderCheckbox('SC-Arunthathiyar', 'SCArunthathiyar')}
            {renderCheckbox('OC', 'oc')}
            {renderCheckbox('DNC (Denotified Communities)', 'dnc')}
            {renderCheckbox('Social category not disclosed', 'socialCategory')}
          </div>
        </div>
      )}

      {/* Classes section */}
      {showClasses && (
        <div className="shared-filter-section">
          <label className="shared-filter-section-label">Classes</label>
          <div className="shared-filter-options shared-filter-scrollable">
            <div className="shared-filter-item">
              <Checkbox
                inputId="allClassesChk"
                checked={allClassesChecked}
                onChange={(e: CheckboxChangeEvent) =>
                  handleAllClasses(e.checked ?? false)
                }
              />
              <label htmlFor="allClassesChk" className="shared-filter-label">
                All
              </label>
            </div>
            {renderCheckbox('Pre Kg', 'pre_kg')}
            {renderCheckbox('Lkg', 'lkg')}
            {renderCheckbox('Ukg', 'ukg')}
            {renderCheckbox('Class1', 'class1')}
            {renderCheckbox('Class2', 'class2')}
            {renderCheckbox('Class3', 'class3')}
            {renderCheckbox('Class4', 'class4')}
            {renderCheckbox('Class5', 'class5')}
            {renderCheckbox('Class6', 'class6')}
            {renderCheckbox('Class7', 'class7')}
            {renderCheckbox('Class8', 'class8')}
            {renderCheckbox('Class9', 'class9')}
            {renderCheckbox('Class10', 'class10')}
            {renderCheckbox('Class11', 'class11')}
            {renderCheckbox('Class12', 'class12')}
          </div>
        </div>
      )}
    </div>
  );
};

export default SharedFilter;
